import type { FeatureConfig } from '~/lib/builder-types';
import type { TechSpecResolver } from './types';

export const authTechSpec: TechSpecResolver = (
  stack,
  featureConfig?: FeatureConfig,
) => {
  const isMongo = stack.database === 'mongodb';
  const isClerk = stack.authProvider === 'auth0-clerk';
  const isDevise = stack.authProvider === 'devise';
  const isPassport = stack.authProvider === 'passport';

  const methods = (featureConfig?.options['auth-method'] as
    | string[]
    | undefined) ?? ['email-password', 'oauth'];
  const hasOAuth = methods.includes('oauth');
  const hasSAML = methods.includes('saml');
  const hasMFA =
    (featureConfig?.options['mfa'] as string | undefined) !== 'none';

  return {
    pages: [
      {
        id: 'login',
        path: '/auth/login',
        label: 'Login',
        description: 'Email/password and OAuth sign-in form',
        authRequired: false,
      },
      {
        id: 'register',
        path: '/auth/register',
        label: 'Register',
        description: 'Account creation form',
        authRequired: false,
      },
      {
        id: 'forgot-password',
        path: '/auth/forgot-password',
        label: 'Forgot password',
        description: 'Send password reset email',
        authRequired: false,
      },
      {
        id: 'reset-password',
        path: '/auth/reset-password',
        label: 'Reset password',
        description: 'Set new password via emailed token',
        authRequired: false,
      },
      ...(hasMFA
        ? [
            {
              id: 'mfa-setup',
              path: '/settings/security',
              label: 'Security settings',
              description: 'Enable TOTP or SMS-based MFA',
              authRequired: true,
            },
          ]
        : []),
      ...(hasSAML
        ? [
            {
              id: 'sso',
              path: '/auth/sso',
              label: 'SSO login',
              description: 'SAML/SSO entry point for enterprise accounts',
              authRequired: false,
            },
          ]
        : []),
    ],
    uxFlows: [
      {
        id: 'email-signup',
        title: 'Email/password sign-up',
        steps: isClerk
          ? [
              'User opens /auth/register',
              'Clerk-hosted UI handles registration',
              'Clerk fires webhook → app creates user record if needed',
              'User is redirected to dashboard with session',
            ]
          : isDevise
            ? [
                'User submits registration form',
                'Devise validates and creates User record',
                'Confirmation email sent via ActionMailer',
                'User clicks email link → account activated → redirected to dashboard',
              ]
            : [
                'User submits registration form',
                'POST /api/auth/register hashes password (bcrypt) and creates user',
                'Verification email sent',
                'User clicks email link → PATCH /api/auth/verify sets verified=true',
                'User signs in → session cookie issued',
              ],
      },
      ...(hasOAuth
        ? [
            {
              id: 'oauth-login',
              title: 'OAuth sign-in (e.g. Google)',
              steps: [
                'User clicks "Sign in with Google"',
                'GET /api/auth/oauth/google redirects to Google consent screen',
                'Google redirects back to /api/auth/callback/google with code',
                'Server exchanges code for access token, fetches user profile',
                isClerk
                  ? 'Clerk merges OAuth identity with existing account if email matches'
                  : 'Server upserts user record, issues session cookie',
                'User is redirected to dashboard',
              ],
            },
          ]
        : []),
    ],
    endpoints: isClerk
      ? [
          {
            id: 'clerk-webhook',
            method: 'POST',
            path: '/api/webhooks/clerk',
            description: 'Clerk webhook — sync user events to app DB',
            requestBody: 'Clerk event payload (signed)',
          },
        ]
      : isDevise
        ? []
        : [
            {
              id: 'register',
              method: 'POST',
              path: '/api/auth/register',
              description: 'Create new account',
              requestBody: '{ email, password, name }',
              response: '{ userId }',
            },
            {
              id: 'login',
              method: 'POST',
              path: '/api/auth/login',
              description: 'Sign in and receive session cookie',
              requestBody: '{ email, password }',
              response: '{ user }',
            },
            {
              id: 'logout',
              method: 'POST',
              path: '/api/auth/logout',
              description: 'Invalidate session',
            },
            {
              id: 'me',
              method: 'GET',
              path: '/api/auth/me',
              description: 'Return current authenticated user',
              response: '{ user }',
            },
            ...(hasOAuth
              ? [
                  {
                    id: 'oauth-start',
                    method: 'GET' as const,
                    path: '/api/auth/oauth/:provider',
                    description: 'Initiate OAuth flow — redirect to provider',
                  },
                  {
                    id: 'oauth-callback',
                    method: 'GET' as const,
                    path: '/api/auth/callback/:provider',
                    description:
                      'OAuth callback — exchange code, create session',
                  },
                ]
              : []),
            {
              id: 'forgot-password',
              method: 'POST',
              path: '/api/auth/forgot-password',
              description: 'Send password reset email',
              requestBody: '{ email }',
            },
            {
              id: 'reset-password',
              method: 'POST',
              path: '/api/auth/reset-password',
              description: 'Apply new password via reset token',
              requestBody: '{ token, password }',
            },
          ],
    dbTables: isClerk
      ? [
          {
            id: 'users',
            name: 'users',
            columns: isMongo
              ? [
                  { name: '_id', type: 'ObjectId', nullable: false },
                  { name: 'clerkId', type: 'string', nullable: false },
                  { name: 'email', type: 'string', nullable: false },
                  { name: 'name', type: 'string', nullable: true },
                  { name: 'createdAt', type: 'Date', nullable: false },
                ]
              : [
                  { name: 'id', type: 'uuid', nullable: false },
                  {
                    name: 'clerk_id',
                    type: 'text',
                    nullable: false,
                    notes: 'Clerk user ID for webhook sync',
                  },
                  { name: 'email', type: 'text', nullable: false },
                  { name: 'name', type: 'text', nullable: true },
                  { name: 'created_at', type: 'timestamp', nullable: false },
                ],
            notes:
              'Clerk manages auth state; this table syncs essential fields',
          },
        ]
      : isMongo
        ? [
            {
              id: 'users',
              name: 'users',
              columns: [
                { name: '_id', type: 'ObjectId', nullable: false },
                { name: 'email', type: 'string', nullable: false },
                { name: 'name', type: 'string', nullable: true },
                { name: 'passwordHash', type: 'string', nullable: true },
                { name: 'emailVerified', type: 'boolean', nullable: false },
                { name: 'createdAt', type: 'Date', nullable: false },
              ],
            },
            {
              id: 'sessions',
              name: 'sessions',
              columns: [
                { name: '_id', type: 'ObjectId', nullable: false },
                { name: 'userId', type: 'ObjectId ref users', nullable: false },
                { name: 'token', type: 'string', nullable: false },
                { name: 'expiresAt', type: 'Date', nullable: false },
              ],
            },
          ]
        : [
            {
              id: 'users',
              name: 'users',
              columns: [
                { name: 'id', type: 'uuid', nullable: false },
                { name: 'email', type: 'text', nullable: false },
                { name: 'name', type: 'text', nullable: true },
                { name: 'password_hash', type: 'text', nullable: true },
                { name: 'email_verified', type: 'boolean', nullable: false },
                { name: 'created_at', type: 'timestamp', nullable: false },
              ],
            },
            {
              id: 'sessions',
              name: 'sessions',
              columns: [
                { name: 'id', type: 'uuid', nullable: false },
                { name: 'user_id', type: 'uuid FK users', nullable: false },
                { name: 'token', type: 'text', nullable: false },
                { name: 'expires_at', type: 'timestamp', nullable: false },
              ],
            },
            ...(hasOAuth
              ? [
                  {
                    id: 'oauth_accounts',
                    name: 'oauth_accounts',
                    columns: [
                      { name: 'id', type: 'uuid', nullable: false },
                      {
                        name: 'user_id',
                        type: 'uuid FK users',
                        nullable: false,
                      },
                      { name: 'provider', type: 'text', nullable: false },
                      {
                        name: 'provider_account_id',
                        type: 'text',
                        nullable: false,
                      },
                    ],
                    notes: 'Unique on (provider, provider_account_id)',
                  },
                ]
              : []),
          ],
  };
};
